import React, { useState, useMemo } from 'react';

const REPORTS = [
  { key: 'daily-summary',       label: 'Daily Summary',       desc: 'Daily totals by category & department' },
  { key: 'department',          label: 'Department Report',   desc: 'Spending by department for a period' },
  { key: 'vendor',              label: 'Vendor Report',       desc: 'Spending by vendor with ITC summary' },
  { key: 'payment-mode',        label: 'Payment Mode Report', desc: 'Cash vs bank vs UPI breakdown' },
  { key: 'petty-cash',          label: 'Petty Cash Register', desc: 'All petty-cash movements with variance' },
  { key: 'expense-vs-revenue',  label: 'Expense vs Revenue',  desc: 'Revenue (rooms/F&B/banquet) vs expenses' },
  { key: 'budget-variance',     label: 'Budget Variance',     desc: 'Budget vs actual by category × month' },
  { key: 'profitability',       label: 'Profitability Analysis', desc: 'Cost-mapped P&L by cost center' },
  { key: 'cost-heatmap',        label: 'Cost Heatmap',        desc: 'Department × week heatmap' },
  { key: 'itc-summary',         label: 'ITC Summary',         desc: 'Input Tax Credit eligibility & claim summary' },
  { key: 'tds-summary',         label: 'TDS Summary',         desc: 'TDS deductions by section code' },
  { key: 'no-invoice-register', label: 'No-Invoice Register', desc: 'Expenses without supplier invoice (statutory auditor view)' },
  { key: 'audit-trail',         label: 'Audit Trail',         desc: 'HMAC-signed state-change log' }
];

const PAGE_SIZE = 25;

function fmtMoney(n) {
  return `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtCell(value, col) {
  if (value == null || value === '') return '—';
  if (col.format === 'money') return fmtMoney(value);
  return String(value);
}

function PmsExpenseReports({ token, selectedProperty }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';
  const [activeReport, setActiveReport] = useState(REPORTS[0].key);
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const run = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    setLoading(true);
    setError('');
    setData(null);
    setPage(0);
    try {
      const qs = new URLSearchParams({ from, to });
      const res = await fetch(`/api/v1/expense/reports/${activeReport}?${qs.toString()}`, { headers: headers() });
      const json = await res.json();
      if (res.status === 501) {
        setError(`${REPORTS.find(r => r.key === activeReport)?.label} — endpoint stubbed (next slice). SRS shape documented; UI will render the same table once backend lands.`);
        return;
      }
      if (!res.ok) return setError(json.error || 'Run failed');
      setData(json);
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  };

  const downloadFile = async (format, ext) => {
    setError('');
    try {
      const qs = new URLSearchParams({ from, to, format });
      const res = await fetch(`/api/v1/expense/reports/${activeReport}?${qs.toString()}`, { headers: headers() });
      if (res.status === 501) return setError('Export available when this report endpoint lands.');
      if (!res.ok) { const j = await res.json().catch(() => ({})); return setError(j.error || `${ext.toUpperCase()} failed`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeReport}-${from}-to-${to}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { setError('Network error'); }
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' };

  // Paginate rows — useMemo keyed off data + page so we don't rebuild on every keystroke
  const pageRows = useMemo(() => (data?.rows || []).slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [data, page]);
  const totalRows = data?.rows?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  // Heuristic: if a key contains 'amount'/'total'/'budget'/'variance' or matches common money cols,
  // right-align numerically. Backend already sets col.align/col.format for newer reports.
  const inferAlign = (col) => col.align || (col.format === 'money' ? 'right' : (/amount|total|budget|variance|count|n|qty/i.test(col.key) ? 'right' : 'left'));

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Expense Reports</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem', borderLeft: '4px solid #dc2626' }}>⚠ {error}</div>}

      {/* Report tabs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.8rem' }}>
        {REPORTS.map(r => (
          <button key={r.key}
            onClick={() => { setActiveReport(r.key); setData(null); setError(''); setPage(0); }}
            style={{
              padding: '0.4rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 999,
              background: activeReport === r.key ? '#2563eb' : '#fff',
              color: activeReport === r.key ? '#fff' : '#374151',
              cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600
            }}>{r.label}</button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem' }}>
        <div style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
          <strong style={{ color: '#111827' }}>{REPORTS.find(r => r.key === activeReport)?.label}</strong>
          {' — '}
          {REPORTS.find(r => r.key === activeReport)?.desc}
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}><span>From</span><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}><span>To</span><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></label>
          <button onClick={run} disabled={loading} style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>{loading ? 'Running…' : 'Run Report'}</button>
          <button onClick={() => downloadFile('pdf', 'pdf')} disabled={!data} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: data ? '#fff' : '#f9fafb', cursor: data ? 'pointer' : 'not-allowed', color: data ? '#374151' : '#9ca3af' }}>Download PDF</button>
          <button onClick={() => downloadFile('csv', 'csv')} disabled={!data} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: data ? '#fff' : '#f9fafb', cursor: data ? 'pointer' : 'not-allowed', color: data ? '#374151' : '#9ca3af' }}>Download CSV</button>
        </div>
      </div>

      {/* Results */}
      {!data ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          {loading ? 'Generating report…' : 'Pick a date range and click Run Report.'}
        </div>
      ) : !Array.isArray(data.columns) || !Array.isArray(data.rows) ? (
        // Fallback for reports that return a different shape (none currently, but safe-guard)
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem' }}>
          <pre style={{ fontSize: '0.78rem', overflow: 'auto', maxHeight: 600, fontFamily: 'monospace' }}>{JSON.stringify(data, null, 2)}</pre>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {/* Toolbar */}
          <div style={{ padding: '0.6rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#fafafa', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem' }}>
            <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>
              {totalRows} record{totalRows === 1 ? '' : 's'}
            </span>
            {data.period && (
              <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                Period: {data.period.from} → {data.period.to}
              </span>
            )}
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                  {data.columns.map(c => (
                    <th key={c.key} style={{ padding: '0.55rem', textAlign: inferAlign(c), whiteSpace: 'nowrap', borderBottom: '1px solid #d1d5db' }}>{c.label || c.key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={data.columns.length} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No data for this period.</td></tr>
                ) : pageRows.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                    {data.columns.map(c => (
                      <td key={c.key} style={{ padding: '0.5rem', textAlign: inferAlign(c), verticalAlign: 'top', whiteSpace: c.key === 'reason' || c.key === 'purpose' || c.key === 'top_vendors' ? 'normal' : 'nowrap' }}>
                        {fmtCell(r[c.key], c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {Array.isArray(data.summary) && data.summary.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#fef3c7', borderTop: '2px solid #f59e0b' }}>
                    <td colSpan={data.columns.length} style={{ padding: '0.6rem 0.8rem' }}>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.85rem', fontWeight: 600, color: '#78350f' }}>
                        {data.summary.map((s, i) => (
                          <span key={i}>
                            <span style={{ color: '#92400e', fontWeight: 500 }}>{s.label}:</span>{' '}
                            <span>{s.format === 'money' ? fmtMoney(s.value) : (s.value ?? '—')}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Pagination */}
          {totalRows > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 1rem', borderTop: '1px solid #e5e7eb', background: '#fafafa' }}>
              <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalRows)} of {totalRows} · Page {page + 1} of {totalPages}
              </span>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button onClick={() => setPage(0)} disabled={page === 0} style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer' }}>« First</button>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer' }}>‹ Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}>Next ›</button>
                <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}>Last »</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PmsExpenseReports;
